/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Form,
  Select as AntdSelect,
  Button,
  message,
  Card,
  Row,
  Col,
  DatePicker
} from 'antd';
import { useForm, useController, Controller } from 'react-hook-form';
import { billingAlertsSchema } from '../schema';
import { get, post } from '../../../utils/httpRequests';
import { APIs } from '../../../utils/apis';
import moment from 'moment';

const Select = ({ control, name, rules = {}, ...rest }) => {
  const {
    field: { onChange, onBlur, value, name: fieldName, ref }
  } = useController({ control, name, rules });
  return (
    <AntdSelect
      {...rest}
      name={fieldName}
      onChange={onChange}
      value={value}
      ref={ref}
      onBlur={onBlur}
    />
  );
};

const configurationInit = {
  list: [],
  isFetching: true
};

const CreateForm = props => {
  const [state, setState] = useState({
    ...configurationInit
  });
  const { handleSubmit, control, formState, reset, setValue } = useForm({
    resolver: billingAlertsSchema,
    mode: 'onChange'
  });

  useEffect(() => {
    // Redirect to the dashboard configuration page if editing an entry without a valid ID
    if (props.match.params.action === 'edit' && !props.match.params.id) {
      props.history.push('/billing/alerts');
    }
  }, [props.match.params.id, props.match.params.action]);

  useEffect(() => {
    get({
      url: APIs.ACTIVE_REPORTS.META_DATA + '&filters=enterprise'
    }).then(response => {
      if (!response) {
        throw Error(response.statusText);
      }
      const sortedEnterprises =
        response.data.result.enterprises
          .sort((a, b) => a?.name?.localeCompare(b.name))
          .filter(enterprise => enterprise.is_active) || [];
      setState({
        ...state,
        list: sortedEnterprises,
        isFetching: false
      });
    });
  }, []);

  useEffect(() => {
    if (props.match.params.action === 'edit') {
      get({
        url: `${APIs.DASHBOARD_BILLING_ALERTS}id=${props.match.params.id}`
      }).then(response => {
        if (
          response.data.meta.status === 200 &&
          response.data.result.list.length > 0
        ) {
          const record = response.data.result.list[0];
          if (record.enterpriseId) {
            setValue('enterpriseId', [record.enterpriseId]);
            setValue('dueDate', moment(record.dueDate));
          }
        }
      });
    } else if (props.match.params.id) {
      setValue('enterpriseId', [Number(props.match.params.id)]);
    }
  }, [props.match.params.id]);

  const formItemWithErrorProps = useCallback(
    name => {
      const error = formState.errors[name];
      if (!error) return { name };
      return {
        name,
        validateStatus: 'error',
        help: error.message
      };
    },
    [formState.errors]
  );

  const onSubmit = async formData => {
    const { enterpriseId, dueDate } = formData;
    const promises = enterpriseId.map(id => {
      return post({
        url: APIs.DASHBOARD_BILLING_ALERTS,
        data: {
          dueDate: moment(dueDate).format('YYYY-MM-DD'),
          enterpriseId: Number(id),
          show: true
        }
      });
    });

    try {
      const responses = await Promise.all(promises);
      const allSuccess = responses.every(
        res => res.data.meta.status === 201 || res.data.meta.status === 200
      );
      if (allSuccess) {
        message.success('Billing alerts have been successfully created');
        props.history.push('/billing/alerts/');
      } else {
        message.error('One or more billing alerts could not be created');
      }
    } catch (error) {
      message.error('An error occurred while processing your request.');
    }
  };

  const onClear = () => {
    reset({});
  };

  return (
    <>
      <Row className="margin-bottom-20">
        <Col span={16} offset={4}>
          <NavLink to="/billing/alerts" className="ant-btn">
            Go Back
          </NavLink>
        </Col>
      </Row>
      <Row>
        <Col span={16} offset={4}>
          <Card title="Billing Alerts Setup">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Item
                {...formItemWithErrorProps('enterpriseId')}
                label="Enterprise"
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select Enterprise"
                  optionFilterProp="children"
                  allowClear={false}
                  control={control}
                  name={'enterpriseId'}
                >
                  {state.list.map(item => (
                    <AntdSelect.Option value={item.id} key={item.id}>
                      {item.name}
                    </AntdSelect.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Due Date"
                validateStatus={
                  formState?.errors?.dueDate?.message ? 'error' : ''
                }
                help={
                  formState?.errors?.dueDate?.message
                    ? 'Please select a due date'
                    : ''
                }
              >
                <Controller
                  name="dueDate"
                  control={control}
                  render={({ field }) => (
                    <>
                      <DatePicker
                        {...field}
                        onChange={date => field.onChange(date)}
                        style={{ width: '200px' }}
                      />
                    </>
                  )}
                />
              </Form.Item>

              <Form.Item className="margin-top-20 margin-bottom-0">
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
                <Button
                  type="ghost"
                  htmlType="reset"
                  onClick={onClear}
                  style={{ marginLeft: '20px' }}
                >
                  Clear
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CreateForm;
